import { t } from 'i18next';
import { SURCHARGE_TYPES, VALUE_TYPE } from './surcharge-helpers';

const pdfModel = (products, purchaseDetails, companyLogo, selflyLogo, orgDetails) => {

    // calculates discount amount
    const totalOriginalAmount = products.reduce((acc, p) => acc + (p.originalPrice || p.price) * p.count, 0);
    const totalDiscountAmount = products.some(p => p.discountAmount)
        ? Math.round((totalOriginalAmount - purchaseDetails.totalSum) * 100) / 100
        : 0;

    // calculates total VAT amount.
    const totalVATAmount = () => {
        const vatAmount = products.reduce((accumulator, product) => {
            const totalPrice = product.price * product.count
            const netPrice = product.vatValue ? (100 * totalPrice) / (100 + product.vatValue) : 0
            const VATAmount = product.vatValue ? (product.vatValue / 100) * (netPrice) : 0

            return accumulator + VATAmount
        }, 0)
        if (Number(vatAmount)) return `${t('vatAmount')}: ${products[0].currency} ${vatAmount.toFixed(2)}`
        else return '';
    }

    const totalSurchargeAmount = () => {
        const surchargeAmount = products.reduce((accumulator, product) => {
            const totalPrice = product.totalSurchargeAmount * product.count;
            return accumulator + totalPrice;
        }, 0);

        if (Number(surchargeAmount)) {
            return `${t('surchargeAmount')}: ${products[0].currency} ${surchargeAmount.toFixed(2)}`
        } else {
            return '';
        }
    }

    const totalDepositAmount = () => {
        const depositAmount = products.reduce((accumulator, product) => {
            const totalPrice = product.totalDepositAmount * product.count;
            return accumulator + totalPrice;
        }, 0);

        if (Number(depositAmount)) {
            return `${t('depositAmount')}: ${products[0].currency} ${depositAmount.toFixed(2)}`
        } else {
            return '';
        }
    }

    // generates organization details.
    const generateOrgDetails = () => {
        const textStack = [];

        if (!orgDetails) {
            return textStack;
        }
        const { organisationName, businessId, street, city, country, website, email, phoneNumber, postalCode } = orgDetails;

        if (organisationName) {
            textStack.push({ text: organisationName, bold: true });
        }
        if (businessId) {
            textStack.push({ text: businessId });
        }
        if (street && postalCode) {
            textStack.push({ text: `${street} ${postalCode}` });
        }
        if (!street && postalCode) {
            textStack.push({ text: postalCode });
        }
        if (!postalCode && street) {
            textStack.push({ text: street });
        }
        if (city && country) {
            textStack.push({ text: `${city} ${country}` });
        }
        if (!city && country) {
            textStack.push({ text: country });
        }
        if (!country && city) {
            textStack.push({ text: city });
        }
        if (phoneNumber) {
            textStack.push({ text: phoneNumber });
        }
        if (email) {
            textStack.push({ text: email });

        }
        if (website) {
            textStack.push({ text: website });
        }

        return textStack;
    }

    const images = {};
    if (companyLogo) images.orgLogo = companyLogo;
    if (selflyLogo) images.selflyLogo = selflyLogo;

    const generateSummedRows = () => {
        const stack = [];
        if (purchaseDetails.status === 'PARTIAL') {
            stack.push({ text: `${t('paidAmount')}: ${products[0].currency} ${purchaseDetails.paidAmount.toFixed(2)}\n` })
            stack.push({ text: `${t('remainingAmount')}: ${products[0].currency} ${purchaseDetails.remainingSum.toFixed(2)}\n` })
        }
        if (totalDiscountAmount > 0) {
            stack.push({ text: `${t('discountAmount')}: ${products[0].currency} ${totalDiscountAmount.toFixed(2)}\n` })
        }

        if (products[0] && products[0].totalSurchargeAmount) {
            stack.push({ text: `${totalSurchargeAmount()}\n` })
        } else {
            // for backward comp.
            stack.push({ text: `${totalVATAmount()}\n` })
        }

        // stack.push({ text: `${totalVATAmount()}\n` })
        // stack.push({ text: `${totalDepositAmount()}\n` })
        return stack;
    }

    const generateProductPriceColumn = (product) => {
        const discountedPrice = product.originalPrice ? product.originalPrice * (1.00 - product.discountAmount / 100) : product.price
        const originalPrice = product.originalPrice ? product.originalPrice : product.price * (1.00 + product.discountAmount / 100)
        if (product.isDiscounted) {
            let originalPriceText = { text: `${product.currency} ${originalPrice.toFixed(2)}`, decoration: 'lineThrough', color: 'red' };
            let discountedPriceText = { text: `${product.currency} ${product.discountAmountType && product.discountAmountType === 'fixed' ? product.price.toFixed(2) : discountedPrice.toFixed(2)}`, color: 'green' }
            return { text: [originalPriceText, { text: ' ' }, discountedPriceText] };
        } else {
            return { text: `${product.currency} ${product.price.toFixed(2)}` };
        }
    }

    const generateProductsTotalPriceColumn = (product) => {
        const discountedPrice = product.originalPrice ? product.originalPrice * (1.00 - product.discountAmount / 100) : product.price
        if (product.isDiscounted) {
            return { text: `${product.currency} ${((product.discountAmountType && product.discountAmountType === 'fixed' ? product.price : discountedPrice) * product.count).toFixed(2)}` }
        } else {
            return { text: `${product.currency} ${(product.price * product.count).toFixed(2)}` };
        }
    } 

    const generateNameColumnData = (product) => {
        let result = [];
        
        result.push({
            text: product.name ? product.name : '', bold: true 
        });

        if (product.surchargeList && Array.isArray(product.surchargeList) && product.surchargeList.length > 0) {
            product.surchargeList.forEach(sc => {
                let surchargeName;
                if (sc.surchargeType === SURCHARGE_TYPES.VAT) {
                    surchargeName = t('vat');
                } else if (sc.surchargeType === SURCHARGE_TYPES.DEPOSIT) {
                    surchargeName = t('deposit');
                } else {
                    surchargeName = sc.name;
                };
    
                if (sc.valueType === VALUE_TYPE.PERCENTAGE) {
                    surchargeName = surchargeName + ' (' + sc.value + '%)';
                }
    
                result.push([
                    {
                        fontSize: '10', text: `\u200B\t\t${surchargeName}`,
                    },
                ]);
            });
        } else {
            // for backward comp. 
            let surchargeName = t('vat') + ' (' + product.vatValue + '%)';
            result.push([
                {
                    fontSize: '10', text: `\u200B\t\t${surchargeName}`,
                },
            ]);
        }



        return result;
    }

    const generateQuantityColumnData = (product) => {
        let result = [];
        
        result.push({
            text: product.count ? product.count : '',
        });

        if (product.surchargeList && Array.isArray(product.surchargeList) && product.surchargeList.length > 0) {
            product.surchargeList.forEach(sc => {
                result.push([
                    {
                        fontSize: '10', text: product.count ? product.count : '',
                    },
                ])
            });
        } else {
            // for backward comp. 
            result.push([
                {
                    fontSize: '10', text: product.count ? product.count : '',
                },
            ]);
        }

        return result;
    }

    const generateUnitPriceColumnData = (product) => {
        let result = [];

        result.push(generateProductPriceColumn(product));

        if (product.surchargeList && Array.isArray(product.surchargeList) && product.surchargeList.length > 0) {
            product.surchargeList.forEach(surcharge => {
                let surchargeAmount = '-';
                if (surcharge.amount) {
                    surchargeAmount = product.currency + ' ' + Number(surcharge.amount).toFixed(2);
                }
    
                result.push([
                    {
                        fontSize: '10', text: surchargeAmount,
                    },
                ]);
            });
        } else {
            // for backward comp.
            let productBasePrice = Number(((product.price * 100) / (product.vatValue + 100)).toFixed(2));
            let vatAmount = Number((product.price - productBasePrice).toFixed(2));

            result.push([
                {
                    fontSize: '10', text: product.currency + ' ' + vatAmount,
                },
            ]);
        }
       

        return result;
    }

    const generateTotalColumnData = (product) => {
        let result = [];
        
        result.push(generateProductsTotalPriceColumn(product));

        if (product.surchargeList && Array.isArray(product.surchargeList) && product.surchargeList.length > 0) {
            product.surchargeList.forEach(surcharge => {
                let surchargeAmount = '-';
                if (surcharge.amount) {
                    surchargeAmount = product.currency + ' ' + (surcharge.amount * product.count).toFixed(2);
                }
    
                result.push([
                    {
                        fontSize: '10', text: surchargeAmount,
                    },
                ])
            });
        } else {
            // for backward comp.
            let productBasePrice = Number(((product.price * 100) / (product.vatValue + 100)).toFixed(2));
            let vatAmount = Number((product.price - productBasePrice).toFixed(2));
            let totalVATAmount = Number((vatAmount * product.count).toFixed(2));

            result.push([
                {
                    fontSize: '10', text: product.currency + ' ' + totalVATAmount,
                },
            ]);
        }


        return result;
    }

    const generateSurcharges = (products) => {
        let result = [];
        for (let product of products) {
            result.push([
                { 
                    stack: generateNameColumnData(product),
                },
                {
                    stack: generateQuantityColumnData(product),
                },
                {
                    stack: generateUnitPriceColumnData(product),
                },
                {
                    stack: generateTotalColumnData(product),
                }
            ]);
        }
        return result;
    }

    return {
        pageMargins: [20, 20, 70, 0],
        images,
        content: [
            {
                columns: [
                    companyLogo && { alignment: 'left', image: 'orgLogo', width: 75, height: 75, margin: [0, 15, -10, 0] },

                    {
                        alignment: 'left', width: '65%', margin: [0, 15, 0, 0], fontSize: 12, stack: generateOrgDetails()
                    },
                    {
                        alignment: 'left', width: '100%', margin: [-20, 13, 0, 40], fontSize: 12,
                        stack: [
                            {
                                text: t('receipt'),
                                bold: true,
                                fontSize: 15
                            },
                            {
                                text: `#${purchaseDetails.orderId} `,
                            },
                            {
                                text: purchaseDetails.purchaseDate,
                            },
                            {
                                text: purchaseDetails.orderCabinet,
                            },
                            { text: '', margin: [50, 20] }

                        ]
                    },
                ],
                columnGap: 15,
                marginBottom: 40

            },
            {
                layout: 'exampleLayout', // optional
                table: {
                    // headers are automatically repeated if the table spans over multiple pages
                    // you can declare how many rows should be treated as headers
                    headerRows: 1,

                    widths: ['50%', '25%', '25%', '25%'],
                    body: [
                        [t('product'), t('quantityAbrv'), t('unitPrice'), t('total')],
                        ...generateSurcharges(products),
                    ]
                }
            },

            { alignment: 'right', margin: [340, 10, -50, -130], fontSize: 13, stack: generateSummedRows() },

            {
                canvas: [
                    { type: 'line', x1: 555, y1: 150, x2: 410, y2: 150, lineWidth: 1, },

                ]
            },

            { alignment: 'right', bold: true, fontSize: '18', margin: [340, 10, -50, 0], text: `${t('totalCost')}: ${products[0].currency} ${purchaseDetails.totalSum.toFixed(2)} ` },

            { alignment: 'center', fontSize: '20', width: '*', margin: [0, 100, 0, 100], text: t('headerThankYou') },
            selflyLogo && {
                absolutePosition: { x: 20, y: 680 },
                canvas: [
                    { type: 'line', x1: 560, y1: 80, x2: 0, y2: 80, lineWidth: 1, },

                ]
            },
            selflyLogo && {
                absolutePosition: { x: 20, y: 770 },
                columns: [
                    { image: 'selflyLogo', width: 50, height: 50, margin: [0, 5, 0, 0] },
                    { alignment: 'left', width: '64%', margin: [0, 2, 0, 0], fontSize: 10, text: t('selflyTrademark') },

                    {
                        alignment: 'left', width: '36%', fontSize: 10, stack: [
                            {
                                text: t('knowAboutUs')
                            },
                            {
                                margin: [0, 3, 0, 0],
                                text: 'www.selflystore.com',
                                decoration: 'underline',
                                link: 'https://www.selflystore.com'
                            },
                            {
                                margin: [0, 3, 0, 0],
                                text: 'linkedin.com/showcase/selfly-store',
                                decoration: 'underline',
                                link: 'https://www.linkedin.com/showcase/selfly-store'
                            },
                            {
                                margin: [0, 3, 0, 0],
                                text: 'twitter.com/SelflyStore',
                                decoration: 'underline',
                                link: 'https://twitter.com/SelflyStore'
                            },
                        ]
                    }
                ],
                columnGap: 29
            },

        ]

    }
}

export default pdfModel;